import { useEffect, useState } from 'react';
import { getEarnedToday } from '../../api/sparksApi';

/**
 * "Once a day" Sparks, as the server decides it (GET /sparks/:childId/earned-today): a Home Learning lesson and a
 * worksheet's lesson set each earn on the child's first go of the UK day; later goes that day earn nothing.
 * `homeLessons` maps lessonId → today's Home Learning attempts with answers, so a page can tell its own attempt
 * from an earlier one; `worksheetRepeats` lists unfinished worksheets that would earn nothing.
 */
export const REPEAT_COPY = {
  earns: '✨ Earns Sparks',
  doneToday: 'Done today ✓',
  doneTodayHint: 'Sparks again tomorrow',
  homeStart: "You've already earned Sparks for this lesson today! Practise as much as you like, then come back tomorrow to earn more, or try a new lesson.",
  homeFinish: 'No Sparks this time: you did this lesson earlier today. Earn again tomorrow!',
  worksheetChip: 'No Sparks today',
  worksheetStart: "You've already earned Sparks for these lessons today! Practise as much as you like, or pick different lessons to earn more.",
  worksheetFinish: 'No Sparks this time: you did a worksheet on these lessons earlier today. Earn again tomorrow!',
};

const idOf = (v) => String(v?._id ?? v ?? '');

/** True once the lesson has an answered Home Learning attempt today (the rule is on). */
export function homeLessonDoneToday(today, lessonId) {
  return !!today?.homeLearningOncePerDay && (today.homeLessons?.[idOf(lessonId)] ?? []).length > 0;
}

/** True when another Home Learning attempt at this lesson already earned today, so this one earns nothing. */
export function isHomeRepeat(today, lessonId, submissionId) {
  if (!today?.homeLearningOncePerDay) return false;
  return (today.homeLessons?.[idOf(lessonId)] ?? []).some((id) => id !== idOf(submissionId));
}

/** True when this unfinished worksheet's lesson set already earned today. */
export function isWorksheetRepeat(today, worksheetId) {
  return (today?.worksheetRepeats ?? []).includes(idOf(worksheetId));
}

/**
 * The child's earned-today status, or null until it loads (and on any failure: pages then show no chip or line,
 * exactly as before). Change `refreshKey` to fetch again, e.g. after the worksheet list reloads.
 */
export default function useEarnedToday(childId, refreshKey) {
  const [today, setToday] = useState(null);
  useEffect(() => {
    setToday(null);
    if (!childId) return undefined;
    let cancelled = false;
    getEarnedToday(childId)
      .then((t) => { if (!cancelled) setToday(t); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [childId, refreshKey]);
  return today;
}
